import React from 'react';
import { useForm } from 'react-hook-form';
import { AssessmentPoint } from '../../types';
import Input from '../Form/Input';
import TextArea from '../Form/TextArea';
import Select from '../Form/Select';
import Button from '../Form/Button';

interface PointsFormProps {
  point?: AssessmentPoint;
  onSubmit: (data: Partial<AssessmentPoint>) => void;
  onCancel: () => void;
}

interface PointsFormData {
  name: string;
  description: string;
  category: 'technical' | 'communication' | 'experience' | 'education' | 'leadership' | 'other';
  weight: number;
  applicableFor: 'candidates' | 'employees' | 'hiring' | 'both';
  isActive: boolean;
}

const PointsForm: React.FC<PointsFormProps> = ({ point, onSubmit, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<PointsFormData>({
    defaultValues: point ? {
      name: point.name,
      description: point.description,
      category: point.category,
      weight: point.weight,
      applicableFor: point.applicableFor,
      isActive: point.isActive
    } : {
      name: '',
      description: '',
      category: 'technical',
      weight: 1,
      applicableFor: 'both',
      isActive: true
    }
  });

  const categoryOptions = [
    { value: 'technical', label: 'Technical Skills' },
    { value: 'communication', label: 'Communication' },
    { value: 'experience', label: 'Experience' },
    { value: 'education', label: 'Education' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'other', label: 'Other' }
  ];

  const weightOptions = [
    { value: '1', label: '1 - Low Importance' },
    { value: '2', label: '2 - Below Average' },
    { value: '3', label: '3 - Average' },
    { value: '4', label: '4 - High Importance' },
    { value: '5', label: '5 - Critical' }
  ];

  const applicableForOptions = [
    { value: 'candidates', label: 'Candidates Only (recruitment)' },
    { value: 'employees', label: 'Employees Only (crew evaluation)' },
    { value: 'both', label: 'Both Candidates & Employees' },
    // Legacy value kept so old records still display correctly when edited
    ...(point?.applicableFor === 'hiring' ? [{ value: 'hiring', label: 'Hiring (legacy)' }] : []),
  ];

  const handleFormSubmit = (data: PointsFormData) => {
    const submitData = {
      ...data,
      weight: Number(data.weight)
    };
    onSubmit(submitData);
  };

  return (
    <div className="bg-white shadow-sm rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="mb-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {point ? 'Edit Assessment Point' : 'Add New Assessment Point'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {point ? 'Update the assessment point details below.' : 'Create a new assessment point for evaluating candidates and hiring.'}
          </p>
        </div>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="border-b border-gray-200 pb-6">
            <h4 className="text-md font-medium text-gray-900 mb-4">Basic Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Point Name *"
                {...register('name', { required: 'Point name is required' })}
                error={errors.name?.message}
                placeholder="e.g., Technical Expertise"
              />

              <Select
                label="Category *"
                {...register('category', { required: 'Category is required' })}
                error={errors.category?.message}
                options={categoryOptions}
              />

              <Select
                label="Weight (Importance) *"
                {...register('weight', { required: 'Weight is required' })}
                error={errors.weight?.message}
                options={weightOptions}
              />

              <Select
                label="Applicable For *"
                {...register('applicableFor', { required: 'Applicable for is required' })}
                error={errors.applicableFor?.message}
                options={applicableForOptions}
              />
            </div>

            <div className="mt-6">
              <TextArea
                label="Description"
                {...register('description')}
                error={errors.description?.message}
                placeholder="Describe what this assessment point evaluates..."
                rows={3}
              />
            </div>

            <div className="mt-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Active (Available for use in evaluations)
                </label>
              </div>
            </div>
          </div>

          {/* Information Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Assessment Points Information
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <ul className="list-disc list-inside space-y-1">
                    <li>Each point can be rated from 1 to 10 during evaluation</li>
                    <li>Weight determines the importance of this point (1-5 scale)</li>
                    <li>Points can be used for candidates, hiring, or both processes</li>
                    <li>Inactive points won't appear in evaluation forms</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : point ? 'Update Point' : 'Add Point'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PointsForm;
